import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { MessageCircle, X, Send, Loader2, Minimize2, Maximize2 } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

const SectionChat = ({ sectionTitle, sectionContent, courseTitle, lessonTitle }) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `section-chat-${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Reset chat when section changes
  useEffect(() => {
    setMessages([]);
  }, [sectionTitle, sectionContent]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/section`,
        {
          message: userMessage,
          session_id: sessionId,
          section_title: sectionTitle,
          section_content: sectionContent,
          course_title: courseTitle,
          lesson_title: lessonTitle
        },
        { withCredentials: true }
      );

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get response';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${errorMsg}` }]);
    } finally {
      setIsLoading(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
          isDarkMode 
            ? 'bg-amber-600 hover:bg-amber-500 text-white' 
            : 'bg-amber-500 hover:bg-amber-600 text-white'
        }`}
        data-testid="section-chat-toggle"
        title="Ask about this section"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 flex flex-col shadow-2xl rounded-xl overflow-hidden transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
      } ${isDarkMode ? 'bg-gray-900 border border-amber-700' : 'bg-white border border-amber-300'}`}
      data-testid="section-chat-window"
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${
        isDarkMode ? 'bg-amber-900/50' : 'bg-amber-100'
      }`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MessageCircle className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
          <span className={`font-medium truncate text-sm ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
            {isMinimized ? 'Section Q&A' : `Ask about: ${sectionTitle}`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className={`p-1.5 rounded-md transition-colors ${
              isDarkMode ? 'hover:bg-amber-800/50 text-amber-300' : 'hover:bg-amber-200 text-amber-700'
            }`}
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className={`p-1.5 rounded-md transition-colors ${
              isDarkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-100 text-red-600'
            }`}
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
          }`}>
            {messages.length === 0 && (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium mb-1">Ask Professor Clio</p>
                <p className="text-xs opacity-75">
                  Questions about "{sectionTitle}"
                </p>
              </div>
            )}
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                    msg.role === 'user'
                      ? isDarkMode
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-500 text-white'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-100 border border-gray-700'
                        : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className={`px-3 py-2 rounded-lg ${
                  isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                  <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`p-3 border-t ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask a question..."
                disabled={isLoading}
                className={`flex-1 px-3 py-2 rounded-lg text-sm outline-none transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 text-white placeholder-gray-500 border border-gray-700 focus:border-amber-600'
                    : 'bg-gray-100 text-gray-900 placeholder-gray-400 border border-gray-200 focus:border-amber-500'
                }`}
                data-testid="section-chat-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                size="sm"
                className={`px-3 ${
                  isDarkMode
                    ? 'bg-amber-600 hover:bg-amber-500'
                    : 'bg-amber-500 hover:bg-amber-600'
                } text-white`}
                data-testid="section-chat-send"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SectionChat;
